/**
 * Компонент предпросмотра блока в модальных окнах.
 *
 * Использует ту же DOM-структуру и CSS module стили, что и DashboardBlock,
 * чтобы предпросмотр выглядел идентично блоку на полотне дашборда.
 */

import React, { FC, Ref } from 'react';

import { FormInstance } from 'antd';

import ReportChart from '../../../../../shared/common/components/reports/chart/ReportChart';
import { ReportTable } from '../../../../../shared/common/components/reports/report-table/ReportTable';
import CartNumber from '../../Card';
import { getReportTypeFromDate, chartToTableReportType } from '../constants';

import dashboardStyles from '../../DashboardBlock/DashboardBlock.module.scss';

// Место в state.table.blocks для строк таблицы предпросмотра (см. dataSlotId у ReportTable)
const BLOCK_PREVIEW_DATA_SLOT = '__block_preview__';

// ==================== ТИПЫ ====================

export type BlockDisplayType = 'chart' | 'table' | 'number' | 'heading';

export interface BlockPreviewProps {
    /** Тип отображения блока */
    displayType: BlockDisplayType;
    /** Форма Ant Design */
    form: FormInstance;
    /** Текст заголовка (для heading) */
    heading?: string;
    /** Стили заголовка (для heading) */
    headingStyles?: {
        fontSize: number;
        fontWeight: number;
        color: string;
    };
    /** Высота графика */
    chartHeight?: number;
    /** Максимальное количество строк в таблице */
    maxRows?: number;
    /** Пропсы для CartNumber (number блок) */
    cartNumberProps?: {
        selectedMetrics?: any[];
        selectedGroupings?: any[];
        metricsChecked?: any[];
        attribution?: any;
        filters?: any[];
        chartType?: string;
        groupsChecked?: any[];
        isShow?: boolean;
        generateBlockId?: string;
        details?: string;
        tableData?: any;
        colorRules?: any[];
        [key: string]: any;
    };
    /** Ref для CartNumber */
    cartNumberRef?: Ref<HTMLDivElement>;
}

// ==================== ВЫСОТА PREVIEW-БЛОКА ====================

const PREVIEW_HEIGHTS: Record<BlockDisplayType, number | undefined> = {
    chart: 350,
    table: 300,
    number: undefined, // auto
    heading: undefined, // auto
};

/** Высота header блока (как в DashboardBlock: calc(100% - 45px)) */
const HEADER_HEIGHT = 45;

// ==================== КОМПОНЕНТ ====================

export const BlockPreview: FC<BlockPreviewProps> = ({
    displayType,
    form,
    heading = '',
    headingStyles = {
        fontSize: 24,
        fontWeight: 600,
        color: '#333333',
    },
    chartHeight = 300,
    maxRows = 10,
    cartNumberProps = {},
    cartNumberRef,
}) => {
    const dateValue = form.getFieldValue('date') as string;
    const chartReportType = getReportTypeFromDate(dateValue);
    const tableReportType = chartToTableReportType(chartReportType);
    const blockTitle = form.getFieldValue('title') || '';

    // CSS-классы контейнера — те же, что и у DashboardBlock
    const typeClass = (() => {
        switch (displayType) {
            case 'chart': return dashboardStyles.chartBlock;
            case 'table': return dashboardStyles.tableBlock;
            case 'number': return dashboardStyles.numberBlock;
            case 'heading': return dashboardStyles.heading;
            default: return '';
        }
    })();

    const containerClassName = [
        dashboardStyles.componentContainer,
        dashboardStyles.smoothComponent,
        typeClass,
    ].filter(Boolean).join(' ');

    const previewHeight = PREVIEW_HEIGHTS[displayType];

    // Высота графика = высота контейнера - header (как на дашборде: calc(100% - 45px))
    const effectiveChartHeight = displayType === 'chart'
        ? (previewHeight || 350) - HEADER_HEIGHT
        : chartHeight;

    // Рендер контента в зависимости от типа
    const renderContent = () => {
        switch (displayType) {
            case 'chart':
                return (
                    <ReportChart
                        reportType={chartReportType}
                        displayMode="preview"
                        isModal={true}
                        date={dateValue}
                        hideChartTypeButtons={true}
                        hideTitle={true}
                        chartHeight={effectiveChartHeight}
                    />
                );

            case 'table':
                return (
                    <ReportTable
                        reportType={tableReportType}
                        displayMode="preview"
                        isModal={true}
                        date={dateValue}
                        maxRows={maxRows}
                        hideGroupingsPanel={true}
                        hideMetricsPanel={true}
                        hideSelectionControls={true}
                        hideTotalCheckbox={true}
                        hideMetricButtons={true}
                        disableGroupsLimit={true}
                        hideTotalChosenRow={true}
                        hideRowCheckboxes={true}
                        // Свои строки: иначе предпросмотр (период дашборда) и таблица окна
                        // (последние 7 дней) перезаписывают данные друг друга
                        dataSlotId={BLOCK_PREVIEW_DATA_SLOT}
                    />
                );

            case 'number':
                return (
                    <CartNumber
                        ref={cartNumberRef}
                        {...cartNumberProps}
                        isShow={true}
                        date={dateValue}
                    />
                );

            case 'heading':
                return null; // Заголовок рендерится в headerBlock

            default:
                return null;
        }
    };

    return (
        <div
            className={containerClassName}
            style={{
                overflow: 'hidden',
                boxShadow: 'none',
                height: previewHeight,
                ...(displayType === 'number' ? { minWidth: '180px', maxWidth: '360px' } : {}),
                ...(displayType === 'heading' ? { background: 'transparent', minHeight: '50px' } : {}),
            }}
        >
            {/* Заголовок блока — как на дашборде (marginTop: 0 т.к. нет dragIcon/dropdown) */}
            <div className={dashboardStyles.headerBlock}>
                <div className={dashboardStyles.titleContainer} style={{ marginTop: 0 }}>
                    <h2
                        className={dashboardStyles.title}
                        style={{
                            ...(displayType === 'heading' && headingStyles
                                ? {
                                    fontSize: `${headingStyles.fontSize}px`,
                                    fontWeight: headingStyles.fontWeight,
                                    color: headingStyles.color,
                                }
                                : displayType === 'heading'
                                    ? { fontWeight: 800 }
                                    : {}),
                            marginBottom: 0,
                        }}
                    >
                        {displayType === 'heading' ? (heading || 'Введите текст заголовка') : blockTitle}
                    </h2>
                </div>
            </div>

            {/* Контент блока — стили из CSS module (chartBlock/tableBlock/numberBlock) */}
            <div
                className={dashboardStyles.container}
                style={displayType === 'table' ? { overflow: 'hidden', paddingBottom: 0 } : undefined}
            >
                <div style={{ width: '100%', height: '100%' }}>
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default BlockPreview;
